import { ExternalLink } from 'lucide-react'
import { useRef } from 'react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { DRAG_THRESHOLD } from '../consts'
import type { CanvasLinkItem, CardPosition } from '../types'
import { linkFaviconUrl, linkHostname } from '../utils'

type DragState = {
  pointerId: number
  startClientX: number
  startClientY: number
  originX: number
  originY: number
  moved: boolean
}

export const CanvasLinkCard = ({
  link,
  scale,
  selected,
  onSelect,
  onMove,
  onMoveEnd,
  onContextMenu,
}: {
  link: CanvasLinkItem
  scale: number
  selected: boolean
  onSelect: (opts?: { additive?: boolean }) => void
  onMove?: (position: CardPosition) => void
  onMoveEnd?: () => void
  onContextMenu?: (e: React.MouseEvent) => void
}) => {
  const { t } = useLocalization()
  const dragState = useRef<DragState | null>(null)
  const hostname = linkHostname(link.url)
  const title = link.title || hostname

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.stopPropagation()
    onSelect({ additive: e.shiftKey || e.metaKey || e.ctrlKey })
    if ((e.target as HTMLElement).closest('a')) return
    if (!onMove) return
    dragState.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      originX: link.x,
      originY: link.y,
      moved: false,
    }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      // Pointer may already be released; drag still works via bubbling events.
    }
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragState.current
    if (!drag || drag.pointerId !== e.pointerId) return
    const dxClient = e.clientX - drag.startClientX
    const dyClient = e.clientY - drag.startClientY
    if (!drag.moved && Math.hypot(dxClient, dyClient) > DRAG_THRESHOLD) {
      drag.moved = true
    }
    if (drag.moved && onMove) {
      onMove({
        x: drag.originX + dxClient / scale,
        y: drag.originY + dyClient / scale,
      })
    }
  }

  return (
    <div
      className={cn(
        'absolute left-0 top-0 w-72 select-none overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg',
        'cursor-grab active:cursor-grabbing',
        selected
          ? 'shadow-xl ring-2 ring-blue-500 ring-offset-2'
          : 'hover:shadow-xl'
      )}
      style={{ transform: `translate(${link.x}px, ${link.y}px)` }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={() => {
        if (dragState.current) {
          dragState.current = null
          onMoveEnd?.()
        }
      }}
      onPointerCancel={() => {
        if (dragState.current) {
          dragState.current = null
          onMoveEnd?.()
        }
      }}
      onContextMenu={onContextMenu}
      onDragStart={(e) => e.preventDefault()}
    >
      {link.imageUrl ? (
        <img
          src={link.imageUrl}
          alt=""
          className="block h-36 w-full object-cover"
          draggable={false}
        />
      ) : (
        <Row className="h-24 items-center justify-center bg-gray-50">
          <img
            src={linkFaviconUrl(link.url)}
            alt=""
            className="h-10 w-10 rounded-md"
            draggable={false}
          />
        </Row>
      )}

      <Column className="gap-1 p-3">
        <Row className="items-start gap-2">
          <img
            src={linkFaviconUrl(link.url)}
            alt=""
            className="mt-0.5 h-4 w-4 shrink-0 rounded-sm"
            draggable={false}
          />
          <Column className="min-w-0 flex-1 gap-0.5">
            <p className="truncate text-xs font-medium text-gray-900">{title}</p>
            {link.description ? (
              <p className="line-clamp-2 text-[11px] text-gray-500">
                {link.description}
              </p>
            ) : (
              <p className="truncate text-[11px] text-gray-400">{hostname}</p>
            )}
          </Column>
        </Row>

        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <ExternalLink className="h-3 w-3" />
          {t('Open link', 'Öppna länk')}
        </a>
      </Column>
    </div>
  )
}
